<template>
  <div ref="root" class="notification-panel">
    <button type="button" class="notification-panel__trigger" :aria-expanded="isOpen" aria-label="Notifications"
      @click.stop="togglePopover">
      <i class="material-icons" aria-hidden="true">notifications</i>
      <span class="notification-panel__mobile-label">Notifications</span>
      <span v-if="unreadCount > 0" class="notification-panel__badge">{{ badgeLabel }}</span>
    </button>

    <Teleport to="body">
      <section v-if="isOpen" ref="popover" class="notification-panel__popover" :style="popoverStyle"
        aria-label="Recent notifications" @pointerdown.stop @click.stop>
        <header class="notification-panel__header">
          <div>
            <strong>Notifications</strong>
            <span>{{ unreadCount }} unread</span>
          </div>
          <button type="button" :disabled="unreadCount === 0 || isMutating" @click="markAllRead">Mark all read</button>
        </header>

        <div v-if="isLoading" class="notification-panel__state">Loading notifications…</div>
        <div v-else-if="errorMessage" class="notification-panel__state notification-panel__state--error">{{ errorMessage
        }}</div>
        <div v-else-if="notifications.length === 0" class="notification-panel__state">You’re all caught up.</div>
        <ul v-else class="notification-panel__list">
          <li v-for="notification in notifications" :key="notification.id" class="notification-panel__row">
            <button type="button" class="notification-panel__item"
              :class="{ 'notification-panel__item--unread': !notification.readAt }"
              @click="openNotification(notification)">
              <span class="notification-panel__item-icon material-icons" aria-hidden="true">{{
                iconFor(notification.type) }}</span>
              <span class="notification-panel__item-content">
                <strong>{{ notification.title }}</strong>
                <span class="notification-panel__item-subject">{{ notification.subject.label || notification.subject.id
                }}</span>
                <span class="notification-panel__item-preview">{{ notification.preview }}</span>
                <time>{{ formatTime(notification.createdAt) }}</time>
              </span>
            </button>
            <span :key="notification.readAt ? 'read-actions' : 'unread-actions'"
              class="notification-panel__item-actions" aria-label="Notification actions">
              <button type="button" class="material-icons" aria-label="Notification settings"
                data-tippy-content="Notification settings" data-tippy-placement="top"
                @click.stop="openNotificationSettings">tune</button>
              <button type="button" class="material-icons"
                :aria-label="notification.readAt ? 'Mark as unread' : 'Mark as read'"
                :data-tippy-content="notification.readAt ? 'Mark as unread' : 'Mark as read'" data-tippy-placement="top"
                :disabled="isMutating" @click.stop="toggleNotificationRead(notification)">{{ notification.readAt ?
                  'mark_email_unread' : 'done' }}</button>
              <button type="button" class="material-icons" aria-label="Archive notification"
                data-tippy-content="Archive" data-tippy-placement="top" :disabled="isMutating"
                @click.stop="archiveOne(notification)">archive</button>
            </span>
          </li>
        </ul>

        <footer class="notification-panel__footer">
          <button v-if="canEnablePush" type="button" @click="enablePush">Enable alerts</button>
          <button type="button" :disabled="isMutating || notifications.length === 0" @click="clearRead">Clear
            read</button>
          <button type="button" :disabled="isMutating || notifications.length === 0" @click="clearAll">Clear
            all</button>
        </footer>
      </section>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { Timestamp, Unsubscribe } from 'firebase/firestore';
import {
  archiveNotifications,
  getCachedUnreadCount,
  markNotificationRead,
  markNotificationUnread,
  markNotificationsRead,
  subscribeNotificationSummary,
  subscribeRecentNotifications,
  type UserNotification
} from '@/modules/notifications/NotificationClient';
import {
  enableNotificationPush,
  getPushPermissionState,
  isNotificationPushEnabled
} from '@/modules/notifications/PushNotifications';

const props = defineProps<{ uid: string }>();
const root = ref<HTMLElement | null>(null);
const popover = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const isLoading = ref(true);
const isMutating = ref(false);
const errorMessage = ref('');
const popoverStyle = ref<Record<string, string>>({});
const unreadCount = ref(getCachedUnreadCount(props.uid));
const notifications = ref<UserNotification[]>([]);
const pushPermission = ref(getPushPermissionState());
const pushEnabled = ref(isNotificationPushEnabled());
const badgeLabel = computed(() => unreadCount.value > 99 ? '99+' : String(unreadCount.value));
const canEnablePush = computed(() => !pushEnabled.value && pushPermission.value !== 'denied' && pushPermission.value !== 'unsupported');
let unsubscribeSummary: Unsubscribe | null = null;
let unsubscribeNotifications: Unsubscribe | null = null;

function positionPopover(): void {
  if (window.innerWidth <= 768) {
    popoverStyle.value = {};
    return;
  }

  const trigger = root.value?.querySelector<HTMLElement>('.notification-panel__trigger');
  if (!trigger) return;
  const triggerRect = trigger.getBoundingClientRect();
  popoverStyle.value = {
    left: `${Math.round(triggerRect.right + 8)}px`,
    bottom: `${Math.round(window.innerHeight - triggerRect.top)}px`
  };
}

function togglePopover(): void {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    void nextTick(positionPopover);
  }
}

function handleViewportChange(): void {
  if (isOpen.value) positionPopover();
}

function stopListeners(): void {
  unsubscribeSummary?.();
  unsubscribeNotifications?.();
  unsubscribeSummary = null;
  unsubscribeNotifications = null;
}

function startListeners(): void {
  stopListeners();
  if (!props.uid) return;
  unreadCount.value = getCachedUnreadCount(props.uid);
  unsubscribeSummary = subscribeNotificationSummary(props.uid, count => {
    unreadCount.value = count;
  }, handleError);
  unsubscribeNotifications = subscribeRecentNotifications(props.uid, items => {
    notifications.value = items;
    isLoading.value = false;
    errorMessage.value = '';
  }, handleError);
}

function handleError(error: Error): void {
  console.error('[Notifications] Listener failed:', error);
  errorMessage.value = 'Notifications are temporarily unavailable.';
  isLoading.value = false;
}

function iconFor(type: UserNotification['type']): string {
  if (type === 'mention') return 'alternate_email';
  if (type === 'assignment' || type === 'approval') return 'assignment_ind';
  if (type === 'shipment_milestone') return 'local_shipping';
  return 'chat_bubble';
}

function formatTime(value: Timestamp | null | undefined): string {
  if (!value?.toDate) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(value.toDate());
}

async function runMutation(action: () => Promise<unknown>): Promise<void> {
  if (isMutating.value) return;
  isMutating.value = true;
  errorMessage.value = '';
  try {
    await action();
  } catch (error) {
    handleError(error instanceof Error ? error : new Error('Notification action failed'));
  } finally {
    isMutating.value = false;
  }
}

function markAllRead(): void {
  void runMutation(() => markNotificationsRead({ scope: 'all' }));
}

function toggleNotificationRead(notification: UserNotification): void {
  void runMutation(() => notification.readAt
    ? markNotificationUnread(notification.id)
    : markNotificationRead(notification.id));
}

function archiveOne(notification: UserNotification): void {
  void runMutation(() => archiveNotifications({ ids: [notification.id] }));
}

function openNotificationSettings(): void {
  window.location.assign('/settings#notifications');
}

function clearRead(): void {
  void runMutation(() => archiveNotifications({ scope: 'read' }));
}

function clearAll(): void {
  void runMutation(() => archiveNotifications({ scope: 'all' }));
}

async function enablePush(): Promise<void> {
  await runMutation(async () => {
    const result = await enableNotificationPush();
    pushPermission.value = getPushPermissionState();
    pushEnabled.value = result === 'enabled';
  });
}

async function openNotification(notification: UserNotification): Promise<void> {
  if (!notification.readAt) {
    unreadCount.value = Math.max(0, unreadCount.value - 1);
    await markNotificationRead(notification.id).catch(handleError);
  }
  window.location.assign(notification.actionPath);
}

function handleDocumentPointerDown(event: PointerEvent): void {
  if (!isOpen.value || !(event.target instanceof Node)) return;
  if (root.value?.contains(event.target) || popover.value?.contains(event.target)) return;
  isOpen.value = false;
}

watch(() => props.uid, startListeners);
onMounted(() => {
  startListeners();
  document.addEventListener('pointerdown', handleDocumentPointerDown);
  window.addEventListener('resize', handleViewportChange);
});
onBeforeUnmount(() => {
  stopListeners();
  document.removeEventListener('pointerdown', handleDocumentPointerDown);
  window.removeEventListener('resize', handleViewportChange);
});
</script>
